<template>
    <div class="main">
        <c-panel title="SALAS DE AULA — ÁREA E CAPACIDADE" titleIcon="fas fa-chalkboard">

            <el-alert type="info" :closable="false" show-icon class="u-mb-15"
                title="A capacidade é calculada pelo servidor: área ÷ m² por aluno."
                :description="`Deixando os m² por aluno em branco, vale o padrão da rede (${dados.alunos_por_m2_padrao} m²). A sala é o espaço físico — a mesma sala recebe turmas diferentes em cada turno.`"></el-alert>

            <el-form label-position="top" @submit.native.prevent>
                <el-row :gutter="10" class="u-mb-10">
                    <el-col :xs="24" :sm="10">
                        <el-form-item label="Escola" class="u-mb-0">
                            <el-select v-model="instituicaoId" filterable style="width: 100%;" @change="carregar">
                                <el-option v-for="i in instituicoes" :key="i.id" :value="i.id" :label="i.nome"></el-option>
                            </el-select>
                        </el-form-item>
                    </el-col>
                    <el-col :xs="24" :sm="6">
                        <el-form-item label="Período letivo" class="u-mb-0">
                            <el-select v-model="periodoId" style="width: 100%;" @change="carregar">
                                <el-option v-for="p in periodos" :key="p.id" :value="p.id" :label="p.nome"></el-option>
                            </el-select>
                        </el-form-item>
                    </el-col>
                    <el-col :xs="24" :sm="8" class="u-text-right u-mt-25">
                        <el-button type="primary" size="small" icon="el-icon-plus"
                            :disabled="!instituicaoId || !periodoId" @click="abrirNova">NOVA SALA</el-button>
                    </el-col>
                </el-row>
            </el-form>

            <!--
                Dois números, não um.

                Somar a capacidade das salas de aula com a de banheiro e despensa produziria
                um total que não significa nada. Aqui só entram salas de aula.
            -->
            <el-row :gutter="10" class="u-mb-15" v-if="dados.itens.length">
                <el-col :xs="12" :sm="6"><div class="kpi"><span class="n">{{ dados.totais.salas }}</span><span class="r">Salas</span></div></el-col>
                <el-col :xs="12" :sm="6"><div class="kpi"><span class="n">{{ dados.totais.com_area }}</span><span class="r">Com área informada</span></div></el-col>
                <el-col :xs="12" :sm="6"><div class="kpi"><span class="n">{{ dados.totais.capacidade_total }}</span><span class="r">Capacidade em salas de aula</span></div></el-col>
                <el-col :xs="12" :sm="6"><div class="kpi" :class="{ 'kpi-erro': dados.totais.excedidas > 0 }"><span class="n">{{ dados.totais.excedidas }}</span><span class="r">Salas com turma acima</span></div></el-col>
            </el-row>

            <el-alert v-if="dados.totais.turmas_sem_sala" type="warning" :closable="false" show-icon class="u-mb-15"
                :title="`${dados.totais.turmas_sem_sala} turma(s) desta escola ainda não têm sala definida.`"
                description="Informe a sala no cadastro da turma para que a capacidade seja conferida."></el-alert>

            <el-alert v-if="!dados.itens.length && instituicaoId" type="info" :closable="false" show-icon
                title="Nenhuma sala cadastrada para esta escola neste período."
                description="Use NOVA SALA, ou cadastre pelo próprio formulário da turma."></el-alert>

            <div v-for="s in dados.itens" :key="s.id" class="sala" :class="{ 'sala-excede': s.excede }">
                <div class="sala-cab">
                    <div class="sala-nome">
                        <b>{{ s.nome }}</b>
                        <el-tag v-if="!s.situacao" size="mini" type="info" class="u-ml-5">inativa</el-tag>
                        <el-tag v-if="s.excede" size="mini" type="danger" class="u-ml-5">acima da capacidade</el-tag>
                    </div>
                    <div class="sala-num">
                        <span v-if="s.area_m2">
                            {{ s.area_m2 }} m² ÷ {{ s.alunos_por_m2 || dados.alunos_por_m2_padrao }} m²/aluno =
                            <b>{{ s.capacidade_calculada }} alunos</b>
                        </span>
                        <span v-else class="u-text-muted">sem área informada</span>

                        <el-button size="mini" type="info" plain icon="el-icon-edit" class="u-ml-10" @click="editar(s)"></el-button>
                        <el-button class="u-ml-10" size="mini" type="danger" plain icon="el-icon-delete" @click="excluir(s)"></el-button>
                    </div>
                </div>

                <table v-if="s.turmas.length" class="turmas">
                    <tr v-for="t in s.turmas" :key="t.turma_id" :class="{ excede: t.excede }">
                        <td class="turno">{{ t.turno_nome }}</td>
                        <td>{{ t.turma }}</td>
                        <td class="num">{{ t.vagas }} vagas</td>
                        <td class="num">
                            <span v-if="t.capacidade === null" class="u-text-muted">—</span>
                            <span v-else-if="t.excede" class="alerta">excede em {{ t.excedente }}</span>
                            <span v-else class="ok">cabe</span>
                        </td>
                    </tr>
                </table>
                <div v-else class="sem-turma">Nenhuma turma usa esta sala.</div>
            </div>

            <el-dialog :title="editando ? 'EDITAR SALA' : 'NOVA SALA DE AULA'" :visible.sync="dialog" width="520px">
                <el-form class="u-mt-20" label-position="top" @submit.native.prevent>
                    <el-row :gutter="10">
                        <el-col :xs="24" :sm="24">
                            <el-form-item label="Nome ou identificação da sala">
                                <el-input v-model="form.nome" placeholder="Ex.: Sala 05, Sala Azul, Anexo 2" maxlength="80"></el-input>
                            </el-form-item>
                        </el-col>
                    </el-row>
                    <el-row :gutter="10">
                        <el-col :xs="24" :sm="12">
                            <el-form-item label="Área (m²)">
                                <el-input v-model="form.area_m2" placeholder="48.00"></el-input>
                            </el-form-item>
                        </el-col>
                        <el-col :xs="24" :sm="12">
                            <el-form-item label="m² por aluno">
                                <el-input v-model="form.alunos_por_m2" :placeholder="'Em branco: ' + dados.alunos_por_m2_padrao"></el-input>
                            </el-form-item>
                        </el-col>
                    </el-row>
                    <el-form-item label="Observação">
                        <el-input v-model="form.observacao" type="textarea" :rows="2"></el-input>
                    </el-form-item>
                </el-form>
                <span slot="footer">
                    <el-button size="small" @click="dialog = false">CANCELAR</el-button>
                    <el-button class="u-ml-10" size="small" type="primary" :loading="salvando" @click="salvar">SALVAR</el-button>
                </span>
            </el-dialog>

        </c-panel>
    </div>
</template>

<script>
/**
 * EDUCAÇÃO item 10 — "quantidade de alunos, em sala de aula, por metro quadrado".
 *
 * A tela de "Área e Capacidade (m²/aluno)" trata das dependências do Censo (auditório,
 * cozinha, pátio). Sala de aula NÃO está naquele catálogo — o Censo a conta em campo
 * próprio — e é justamente o espaço que decide vaga. Por isso esta tela existe à parte.
 */
import { periodosLetivosMixin } from '@/shared/periodos-letivos'
export default {
    mixins: [periodosLetivosMixin],
    data: () => ({
        instituicoes: [],
        instituicaoId: null, periodoId: null,
        dados: { itens: [], totais: { salas: 0, com_area: 0, capacidade_total: 0, excedidas: 0, turmas_sem_sala: 0 }, alunos_por_m2_padrao: 1.20 },
        dialog: false, salvando: false, editando: null,
        form: { nome: '', area_m2: '', alunos_por_m2: '', observacao: '' }
    }),

    mounted () {
        this.periodoId = this.$store.state.config.periodo ? this.$store.state.config.periodo.id : null

        this.$axios.get(`${this.$store.state.config.baseUrlApi}/api/instituicao/getAll`,
            this.$store.getters.getHeaderAuth
        ).then(r => {
            this.instituicoes = Array.isArray(r.data) ? r.data : []

            // Usuário de uma escola só: já abre nela, sem exigir a seleção.
            if (this.instituicoes.length === 1) {
                this.instituicaoId = this.instituicoes[0].id
                this.carregar()
            }
        }).catch(() => {})
    },

    methods: {
        url (p) { return `${this.$store.state.config.baseUrlApi}/api/sala-aula/${p}` },

        carregar () {
            if (!this.instituicaoId || !this.periodoId) return

            this.$axios.post(this.url('listar'),
                { instituicao_id: this.instituicaoId, periodo_letivo_id: this.periodoId },
                this.$store.getters.getHeaderAuth
            ).then(r => {
                if (r.data && r.data.erro) { this.erro(r.data.erro); return }
                this.dados = r.data
            }).catch(e => this.erro(e))
        },

        abrirNova () {
            this.editando = null
            this.form = { nome: '', area_m2: '', alunos_por_m2: '', observacao: '' }
            this.dialog = true
        },

        editar (s) {
            this.editando = s.id
            this.form = {
                nome: s.nome,
                area_m2: s.area_m2 !== null ? s.area_m2 : '',
                alunos_por_m2: s.alunos_por_m2 !== null ? s.alunos_por_m2 : '',
                observacao: s.observacao || ''
            }
            this.dialog = true
        },

        salvar () {
            if (!this.form.nome) {
                window.eventBus.$emit('alert', { msg: 'Informe o nome da sala.', type: 'warning' })
                return
            }

            this.salvando = true

            const corpo = Object.assign({
                instituicao_id: this.instituicaoId,
                periodo_letivo_id: this.periodoId
            }, this.form)

            const destino = this.editando ? `update/${this.editando}` : 'store'

            this.$axios.post(this.url(destino), corpo, this.$store.getters.getHeaderAuth)
                .then(() => {
                    this.dialog = false
                    this.carregar()
                    window.eventBus.$emit('alert', { msg: 'Sala salva.', type: 'success' })
                })
                .catch(e => this.erro(e))
                .then(() => { this.salvando = false })
        },

        excluir (s) {
            this.$confirm(`Excluir a sala "${s.nome}"?`, 'Confirmar', { type: 'warning' })
                .then(() => {
                    this.$axios.delete(this.url(`delete/${s.id}`), this.$store.getters.getHeaderAuth)
                        .then(() => {
                            this.carregar()
                            window.eventBus.$emit('alert', { msg: 'Sala excluída.', type: 'success' })
                        })
                        .catch(e => this.erro(e))
                }).catch(() => {})
        },

        erro (e) {
            const d = e && e.response ? e.response.data : null
            window.eventBus.$emit('alert', {
                msg: (d && d.erro) ? d.erro : (typeof e === 'string' ? e : 'Não foi possível concluir a operação.'),
                type: 'error'
            })
        }
    }
}
</script>

<style scoped>
.kpi { background: #f5f7fa; border: 1px solid #e4e7ed; border-radius: 4px; padding: 10px; text-align: center; }
.kpi .n { display: block; font-size: 22px; font-weight: 700; color: #303133; }
.kpi .r { display: block; font-size: 11px; color: #909399; text-transform: uppercase; }
.kpi-erro { background: #fef0f0; border-color: #fbc4c4; }
.kpi-erro .n { color: #f56c6c; }

.sala { border: 1px solid #e4e7ed; border-radius: 4px; margin-bottom: 10px; }
.sala-excede { border-color: #f56c6c; }
.sala-cab { display: flex; justify-content: space-between; align-items: center;
            padding: 8px 12px; background: #f5f7fa; flex-wrap: wrap; }
.sala-excede .sala-cab { background: #fef0f0; }
.sala-nome { font-size: 14px; }
.sala-num { font-size: 12px; color: #606266; }

table.turmas { width: 100%; font-size: 12px; }
table.turmas td { padding: 5px 12px; border-top: 1px solid #f0f2f5; }
table.turmas tr.excede { background: #fff8f8; }
td.turno { width: 100px; color: #909399; text-transform: uppercase; font-size: 11px; }
td.num { width: 130px; text-align: right; }
.alerta { color: #f56c6c; font-weight: 600; }
.ok { color: #67c23a; }
.sem-turma { padding: 8px 12px; font-size: 12px; color: #909399; }
.u-ml-5 { margin-left: 5px; }
.u-ml-10 { margin-left: 10px; }
</style>
